import { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api-client.js";
import { num, pct } from "@/lib/format.js";
import { RoofLayoutSvg } from "@/components/RoofLayoutSvg.js";
import { computeNearShadow } from "@energyos/roof-core";
import type { NearShadowObstacle, NearShadowResult } from "@energyos/roof-core";

interface HorizonShadingData {
  annualShadingLossPct: number;
  monthlyShadingLossPct: number[];
  skyViewFactor: number;
}

interface HorizonResponse {
  profile: { azimuthDeg: number; horizonElevationDeg: number }[];
  horizonAvailable: boolean;
  shading: { data: HorizonShadingData | null; status: string };
}

// Modul-Rechteck wie von gridLayoutPlanar/Belegung persistiert (lokale Meter).
interface GridModule { xM: number; yM: number; wM: number; hM: number; row: number; col: number }
interface PlanningLayout { modules: GridModule[]; roofWidthM: number; roofLengthM: number }

// Editor-Zeile (String-Felder) für ein Nahschatten-Hindernis.
interface ObstacleRow { id: string; label: string; xM: string; yM: string; widthM: string; depthM: string; heightM: string }

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "7px 9px",
  border: "1px solid var(--color-border, #cbd5e1)",
  borderRadius: 6,
  fontSize: 13,
};
const smallInput: React.CSSProperties = { ...inputStyle, padding: "4px 6px", fontSize: 12 };

let obstacleSeq = 0;
const newId = () => `obs-${++obstacleSeq}`;
const f = (v: string): number => {
  const n = parseFloat(v.replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Verschattung v1.1 — zwei Schichten:
 *   A) HORIZONT (fern, automatisch): Geländehorizont aus PVGIS `printhorizon`.
 *   B) NAHVERSCHATTUNG (Dachobjekte): Kamin/Gaube als Rechteck-Footprint +
 *      Höhe; roof-core `computeNearShadow` wirft den Schatten für repräsentative
 *      Sonnenstände auf die Modulbelegung → Verlust-% + getöntes Layout.
 * Karten-frei, deterministisch, Vorbemessung (keine stündliche Jahressimulation).
 */
export function ShadingTab({ projectId }: { projectId: string }) {
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [tiltDeg, setTiltDeg] = useState(30);
  const [azimuthDeg, setAzimuthDeg] = useState(180);
  const [data, setData] = useState<HorizonShadingData | null>(null);
  const [horizonAvailable, setHorizonAvailable] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nahverschattung
  const [layout, setLayout] = useState<PlanningLayout | null>(null);
  const [obstacles, setObstacles] = useState<ObstacleRow[]>([]);

  useEffect(() => {
    void apiClient
      .get<{ buildings: Array<{ latitude?: number; longitude?: number; roofSurfaces?: Array<{ tiltDeg?: number; azimuthDeg?: number; isUsable?: boolean; areaM2?: number }> }> }>(`/projects/${projectId}/geospatial`)
      .then((r) => {
        if (r.success) {
          const b = (r.data.buildings ?? []).find((x) => x.latitude && x.longitude);
          if (b && typeof b.latitude === "number" && typeof b.longitude === "number") {
            setCoords({ lat: b.latitude, lon: b.longitude });
          }
          // Neigung/Azimut aus der größten nutzbaren Dachfläche übernehmen (statt 30/180-Default).
          // RoofSurface.azimuthDeg und die Verschattungs-Engine teilen DIESELBE Konvention
          // (0=N, 90=O, 180=S, 270=W — shading-horizon.ts:35) → KEINE Konvertierung,
          // konsistent mit dem planning-Fetch unten (ld.azimuthDeg wird roh gesetzt).
          const withRoofs = (r.data.buildings ?? []).find((x) => (x.roofSurfaces ?? []).length > 0);
          const surfaces = (withRoofs?.roofSurfaces ?? []).filter((s) => s.isUsable !== false);
          const best = surfaces.slice().sort((a, c) => (c.areaM2 ?? 0) - (a.areaM2 ?? 0))[0];
          if (best && typeof best.tiltDeg === "number" && best.tiltDeg > 0) setTiltDeg(best.tiltDeg);
          if (best && typeof best.azimuthDeg === "number") setAzimuthDeg(best.azimuthDeg);
        }
      });
  }, [projectId]);

  // Neueste Modulbelegung laden (für die Nahschatten-Projektion).
  useEffect(() => {
    void apiClient
      .get<Array<{ layoutData?: { modules?: GridModule[]; roofWidthM?: number; roofLengthM?: number; tiltDeg?: number; azimuthDeg?: number } }>>(
        `/projects/${projectId}/planning`,
      )
      .then((r) => {
        if (!r.success || !Array.isArray(r.data) || r.data.length === 0) return;
        const ld = r.data[0]?.layoutData;
        if (ld && Array.isArray(ld.modules) && typeof ld.roofWidthM === "number" && typeof ld.roofLengthM === "number") {
          setLayout({ modules: ld.modules, roofWidthM: ld.roofWidthM, roofLengthM: ld.roofLengthM });
          if (typeof ld.tiltDeg === "number" && ld.tiltDeg > 0) setTiltDeg(ld.tiltDeg);
          if (typeof ld.azimuthDeg === "number") setAzimuthDeg(ld.azimuthDeg);
        }
      });
  }, [projectId]);

  const compute = async (lat: number, lon: number) => {
    setRunning(true);
    setError(null);
    const res = await apiClient.get<HorizonResponse>(
      `/geo/horizon-shading?lat=${lat}&lon=${lon}&tiltDeg=${tiltDeg}&azimuthDeg=${azimuthDeg}`,
    );
    if (res.success) {
      setData(res.data.shading?.data ?? null);
      setHorizonAvailable(res.data.horizonAvailable);
    } else {
      setError(res.error.message);
    }
    setRunning(false);
  };

  // Automatisch berechnen, sobald Koordinaten vorliegen.
  useEffect(() => {
    if (coords) void compute(coords.lat, coords.lon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords]);

  const maxMonthly = data ? Math.max(0.5, ...data.monthlyShadingLossPct) : 1;

  // ── Nahschatten berechnen (rein client-seitig über roof-core) ───────────────
  const nearObstacles = useMemo<NearShadowObstacle[]>(
    () =>
      obstacles
        .map((o) => ({ id: o.id, label: o.label || "Objekt", xM: f(o.xM), yM: f(o.yM), widthM: f(o.widthM), depthM: f(o.depthM), heightM: f(o.heightM) }))
        .filter((o) => Number.isFinite(o.xM) && Number.isFinite(o.yM) && o.widthM > 0 && o.depthM > 0 && o.heightM > 0),
    [obstacles],
  );

  const nearResult = useMemo<NearShadowResult | null>(() => {
    if (!layout || layout.modules.length === 0 || nearObstacles.length === 0) return null;
    return computeNearShadow({
      modules: layout.modules,
      obstacles: nearObstacles,
      roofTiltDeg: tiltDeg,
      roofAzimuthDeg: azimuthDeg,
      latitudeDeg: coords?.lat ?? 47,
    });
  }, [layout, nearObstacles, tiltDeg, azimuthDeg, coords]);

  // Module mit Verschattungsanteil für die SVG-Tönung.
  const shadedModules = useMemo(() => {
    if (!layout) return [];
    if (!nearResult) return layout.modules.map((m) => ({ ...m }));
    const byKey = new Map(nearResult.perModule.map((pm) => [`${pm.row}:${pm.col}`, pm.shadedFraction]));
    return layout.modules.map((m) => ({ ...m, shadedFraction: byKey.get(`${m.row}:${m.col}`) ?? 0 }));
  }, [layout, nearResult]);

  const obstacleRects = useMemo(
    () => nearObstacles.map((o) => ({ xM: o.xM, yM: o.yM, widthM: o.widthM, depthM: o.depthM, label: o.label })),
    [nearObstacles],
  );

  const addObstacle = () => {
    const cx = layout ? Math.max(0, layout.roofWidthM / 2 - 0.3) : 1;
    const cy = layout ? Math.max(0, layout.roofLengthM * 0.2) : 1;
    setObstacles((prev) => [
      ...prev,
      { id: newId(), label: "Kamin", xM: cx.toFixed(1), yM: cy.toFixed(1), widthM: "0.6", depthM: "0.6", heightM: "1.0" },
    ]);
  };
  const updateObstacle = (id: string, field: keyof ObstacleRow, value: string) =>
    setObstacles((prev) => prev.map((o) => (o.id === id ? { ...o, [field]: value } : o)));
  const removeObstacle = (id: string) => setObstacles((prev) => prev.filter((o) => o.id !== id));

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Verschattung</h2>
      </div>

      <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#0369a1" }}>
        Zwei Schichten: <strong>Horizont</strong> (Gelände, automatisch aus PVGIS) und <strong>Nahverschattung</strong> durch
        Dachobjekte (Kamin/Gaube). Vorbemessung — keine stündliche Jahressimulation; im Detail durch Fachperson prüfen.
      </div>

      {!coords && (
        <div className="card" style={{ textAlign: "center", padding: 32, color: "var(--color-text-muted)" }}>
          Keine Gebäude-Koordinaten — zuerst im Gebäude-Tab einen Standort erfassen.
        </div>
      )}

      {coords && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4, fontWeight: 500 }}>Dachneigung (°)</label>
                <input type="number" min="0" max="90" value={tiltDeg} style={inputStyle}
                  onChange={(e) => setTiltDeg(parseInt(e.target.value, 10))} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginBottom: 4, fontWeight: 500 }}>Azimut (° · 180=Süd)</label>
                <input type="number" min="0" max="360" value={azimuthDeg} style={inputStyle}
                  onChange={(e) => setAzimuthDeg(parseInt(e.target.value, 10))} />
              </div>
              <button className="btn-primary" onClick={() => void compute(coords.lat, coords.lon)} disabled={running}>
                {running ? "Berechne…" : "Horizont neu berechnen"}
              </button>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--color-text-muted)" }}>
              Standort: {num(coords.lat, 4)}°N, {num(coords.lon, 4)}°E
            </div>
          </div>

          {error && (
            <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 13, color: "#dc2626" }}>{error}</div>
          )}

          {/* ── Schicht A: Horizont ─────────────────────────────────────────── */}
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: "4px 0 10px" }}>Horizont (Geländeverschattung)</h3>
          {data && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <div className="card" style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "var(--color-primary)" }}>{pct(data.annualShadingLossPct, 1)}</div>
                  <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>Jahres-Verschattungsverlust (Horizont)</div>
                </div>
                <div className="card" style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "var(--color-primary)" }}>{num(data.skyViewFactor, 2)}</div>
                  <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>Sky-View-Factor (1 = freier Himmel)</div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Monatlicher Verschattungsverlust</h3>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 110 }}>
                  {data.monthlyShadingLossPct.map((v, i) => (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <div style={{ fontSize: 9, color: "var(--color-text-muted)" }}>{num(v, 1)}</div>
                      <div style={{ width: "100%", background: "var(--color-primary)", borderRadius: "3px 3px 0 0", height: `${Math.max(2, (v / maxMonthly) * 80)}px` }} title={`${MONTHS[i]}: ${num(v, 1)} %`} />
                      <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{MONTHS[i]}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, fontSize: 11, color: "var(--color-text-muted)" }}>
                  {horizonAvailable === false
                    ? "Kein Horizontprofil von PVGIS — freier Horizont angenommen (0 %). Bei alpiner Lage manuell prüfen."
                    : "Quelle: PVGIS printhorizon."}
                </div>
              </div>
            </>
          )}

          {/* ── Schicht B: Nahverschattung (Dachobjekte) ────────────────────── */}
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: "4px 0 10px" }}>Nahverschattung (Dachobjekte)</h3>
          {!layout || layout.modules.length === 0 ? (
            <div className="card" style={{ color: "var(--color-text-muted)", fontSize: 13 }}>
              Noch keine Modulbelegung vorhanden — zuerst im Tab <strong>Planung</strong> die Belegung berechnen,
              dann hier Kamin/Gaube als Objekt eintragen.
            </div>
          ) : (
            <div className="card">
              <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 10 }}>
                Objekte auf der Dachfläche (Position ab oberer linker Ecke, in Metern). Schatten wird für repräsentative
                Sonnenstände (Äquinoktium + Wintersonnwende) auf die {layout.modules.length} Module projiziert.
              </div>

              {/* Hindernis-Editor */}
              <div style={{ display: "grid", gridTemplateColumns: "1.4fr repeat(5, 1fr) auto", gap: 6, alignItems: "center", fontSize: 11, color: "var(--color-text-muted)", marginBottom: 4 }}>
                <div>Bezeichnung</div><div>x (m)</div><div>y (m)</div><div>Breite (m)</div><div>Tiefe (m)</div><div>Höhe (m)</div><div />
              </div>
              {obstacles.map((o) => (
                <div key={o.id} style={{ display: "grid", gridTemplateColumns: "1.4fr repeat(5, 1fr) auto", gap: 6, alignItems: "center", marginBottom: 5 }}>
                  <input style={smallInput} value={o.label} onChange={(e) => updateObstacle(o.id, "label", e.target.value)} />
                  <input style={smallInput} type="number" value={o.xM} onChange={(e) => updateObstacle(o.id, "xM", e.target.value)} />
                  <input style={smallInput} type="number" value={o.yM} onChange={(e) => updateObstacle(o.id, "yM", e.target.value)} />
                  <input style={smallInput} type="number" value={o.widthM} onChange={(e) => updateObstacle(o.id, "widthM", e.target.value)} />
                  <input style={smallInput} type="number" value={o.depthM} onChange={(e) => updateObstacle(o.id, "depthM", e.target.value)} />
                  <input style={smallInput} type="number" value={o.heightM} onChange={(e) => updateObstacle(o.id, "heightM", e.target.value)} />
                  <button onClick={() => removeObstacle(o.id)} title="Entfernen"
                    style={{ fontSize: 14, border: "none", background: "none", cursor: "pointer", color: "#b91c1c" }}>🗑</button>
                </div>
              ))}
              <button onClick={addObstacle}
                style={{ fontSize: 12, background: "none", border: "1px dashed #cbd5e1", borderRadius: 6, padding: "4px 12px", cursor: "pointer", color: "#475569", marginTop: 4 }}>
                + Objekt (Kamin/Gaube)
              </button>

              {/* Ergebnis + Visualisierung */}
              <div style={{ display: "flex", gap: 20, marginTop: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ flex: "0 0 auto" }}>
                  <RoofLayoutSvg
                    roofWidthM={layout.roofWidthM}
                    roofLengthM={layout.roofLengthM}
                    modules={shadedModules}
                    obstacles={obstacleRects}
                    {...(nearResult ? { shadowPolygons: nearResult.worstShadowPolys } : {})}
                    maxWidthPx={420}
                  />
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 4 }}>
                    Blau = frei · orange = teilverschattet · rot = stark verschattet · grau = Objekt
                    {nearResult ? ` · Schatten: ${nearResult.worstSampleLabel}` : ""}
                  </div>
                </div>
                <div style={{ flex: "1 1 200px", minWidth: 200 }}>
                  {nearResult ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      <div className="card" style={{ textAlign: "center", padding: 14 }}>
                        <div style={{ fontSize: 26, fontWeight: 700, color: nearResult.nearShadingLossPct > 5 ? "#b45309" : "var(--color-primary)" }}>
                          {pct(nearResult.nearShadingLossPct, 1)}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>Nahschatten-Verlust (Näherung)</div>
                      </div>
                      <div style={{ fontSize: 13 }}>
                        <div>Stark verschattete Module: <strong>{nearResult.shadedModuleCount}</strong> / {layout.modules.length}</div>
                        <div style={{ color: "var(--color-text-muted)", marginTop: 2 }}>Teilweise betroffen: {nearResult.affectedModuleCount}</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
                      Objekt hinzufügen, um den Nahschatten auf der Belegung zu sehen.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
